//! Debug Log Utility - Frontend Logging

import type { LogLevel, LogDomain, LogEntry } from "../entities";

import { useLogStore } from "../hooks/useLogStore";

/**
 * Frontend Debug Log Function
 *
 * Erstellt strukturierte Log-Einträge im Frontend.
 * Diese werden an das Backend gesendet und im Debug-Overlay angezeigt.
 *
 * @param level
 *
 * @param domain
 *
 * @param message
 *
 * @param context
 *
 */
export const debugLog = (
  level: LogLevel,
  domain: LogDomain,
  message: string,
  context?: Record<string, unknown>,
): void => {
  // Erstelle Log-Eintrag mit eindeutiger ID (Timestamp + Secure Random + UUID)
  const timestamp = Date.now();
  // Use cryptographically secure random value instead of Math.random()
  const randomBytes = new Uint8Array(6);
  crypto.getRandomValues(randomBytes);
  const random = Array.from(randomBytes, (byte) => byte.toString(36).padStart(2, "0"))
    .join("")
    .slice(0, 9);
  const uuid = crypto.randomUUID();
  const component = getCurrentComponent();
  const logEntry: LogEntry = {
    context: context ?? {},
    domain,
    id: `frontend-${String(timestamp)}-${random}-${uuid}`,
    level,
    message,
    scope: {
      domain,
    },
    session_id: getSessionId(),
    source: "frontend" as const,
    timestamp: new Date().toISOString(),
  };
  if (component !== undefined) {
    logEntry.component = component;
  }

  // Sende an Backend (falls verfügbar)
  // NOTE: Tauri Event für Frontend Logs is not yet implemented
  // Für jetzt: Log an das Debug-System senden
  sendToDebugSystem(logEntry);
};

/**
 * Debug Log für Health Domain
 *
 * @param level
 *
 * @param module
 *
 * @param message
 *
 * @param context
 *
 */
export const debugHealth = (
  level: LogLevel,
  module: string,
  message: string,
  context?: Record<string, unknown>,
): void => {
  debugLog(level, "health", message, {
    ...context,
    module,
  });
};

/**
 * Debug Log für API Domain
 *
 * @param level
 *
 * @param message
 *
 * @param context
 *
 */
export const debugApi = (
  level: LogLevel,
  message: string,
  context?: Record<string, unknown>,
): void => {
  debugLog(level, "api", message, context);
};

/**
 * Debug Log für UI Domain
 *
 * @param level
 *
 * @param message
 *
 * @param context
 *
 */
export const debugUi = (
  level: LogLevel,
  message: string,
  context?: Record<string, unknown>,
): void => {
  debugLog(level, "ui", message, context);
};

/**
 * Debug Log für System Domain
 *
 * @param level
 *
 * @param message
 *
 * @param context
 *
 */
export const debugSystem = (
  level: LogLevel,
  message: string,
  context?: Record<string, unknown>,
): void => {
  debugLog(level, "system", message, context);
};

/**
 * Debug Log für Productivity Domain
 *
 * @param level
 *
 * @param module
 *
 * @param message
 *
 * @param context
 *
 */
export const debugProductivity = (
  level: LogLevel,
  module: string,
  message: string,
  context?: Record<string, unknown>,
): void => {
  debugLog(level, "productivity", message, {
    ...context,
    module,
  });
};

/**
 * Debug Log für Admin Domain
 *
 * @param level
 *
 * @param module
 *
 * @param message
 *
 * @param context
 *
 */
export const debugAdmin = (
  level: LogLevel,
  module: string,
  message: string,
  context?: Record<string, unknown>,
): void => {
  debugLog(level, "admin", message, {
    ...context,
    module,
  });
};

/**
 * Debug Log für Investments Domain
 *
 * @param level
 *
 * @param message
 *
 * @param context
 *
 */
export const debugInvestments = (
  level: LogLevel,
  message: string,
  context?: Record<string, unknown>,
): void => {
  debugLog(level, "investments", message, context);
};

/**
 * Holt die aktuelle Session-ID
 *
 * @returns Session ID string
 *
 */
const getSessionId = (): string => {
  // NOTE: Session-ID Management is not yet implemented
  return `session-${String(Date.now())}`;
};

/**
 * Holt den aktuellen Component-Namen
 *
 * @returns Component name or undefined
 *
 */
const getCurrentComponent = (): string | undefined => {
  // NOTE: Component-Name Detection is not yet implemented
  // Für jetzt aus dem Stack Trace extrahieren
  const stack = new Error("Component detection").stack;
  if (stack) {
    const lines = stack.split("\n");
    // Suche nach React Component Namen
    for (const line of lines) {
      const match = /at\s+(\w+)/.exec(line);
      if (match?.[1]?.endsWith("Component")) {
        return match[1];
      }
    }
  }
  return undefined;
};

/**
 * Performance Timer für Debug-Logs
 *
 * Timer class for measuring performance and logging duration.
 * Use createDebugTimer() to create instances.
 */
export class DebugTimer {
  private startTime: number;
  private context: Record<string, unknown>;

  /**
   * Creates a new DebugTimer instance
   *
   * Initializes a performance timer that can be used to measure and log
   * the duration of operations.
   *
   * @param context - Optional context object to include in log entry
   *
   */
  constructor(context: Record<string, unknown> = {}) {
    this.startTime = performance.now();
    this.context = context;
  }

  /**
   * Beendet den Timer und loggt die Dauer
   *
   * @param level - Log level
   *
   * @param domain - Log domain
   *
   * @param message - Log message
   *
   */
  end(level: LogLevel, domain: LogDomain, message: string): void {
    const duration = performance.now() - this.startTime;
    debugLog(level, domain, message, {
      ...this.context,
      duration_ms: Math.round(duration),
    });
  }
}

/**
 * Erstellt einen neuen Debug Timer
 *
 * @param context - Optional context for the timer
 *
 * @returns New DebugTimer instance
 *
 */
export const createDebugTimer = (context?: Record<string, unknown>): DebugTimer => {
  return new DebugTimer(context);
};

/**
 * Sendet Log-Eintrag an das Debug-System
 *
 * @param logEntry - Log entry to send
 *
 */
const sendToDebugSystem = (logEntry: LogEntry): void => {
  // Asynchron in den Log Store schreiben, um setState während Render zu vermeiden
  try {
    const logStore = useLogStore.getState();
    setTimeout(() => {
      try {
        logStore.addLog(logEntry);
      } catch {
        // NOTE: Silently ignore log store errors to prevent infinite error loops
      }
    }, 0);
  } catch {
    // Fallback: Log Store noch nicht initialisiert
    // Log wird ignoriert (Zero-Logging Prinzip)
  }
};
